import orderBy from 'lodash/orderBy';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import FieldContainer from '../components/FieldContainer';
import Select from '../components/FixedRequiredSelect';
import { isFieldLocked } from '../components/utils';
import { removeDiacritics } from '../utils/stringUtils';
import './CountrySelectField.scss';
import { StringField } from './index';
import { getCountriesOptions } from '@appcraft/common-utils';

const SaveMode = {
  CODE: 'code',
  LABEL: 'label',
};

const computedCountriesList = (lang, saveMode) => {
  const countries = getCountriesOptions(lang || 'fr');
  return orderBy(
    countries.map((country) => {
      if (saveMode === SaveMode.LABEL) {
        return { ...country, value: country.label };
      }
      return country;
    }),
    (opt) => removeDiacritics(opt.label).toLowerCase(),
  );
};

const memoizedCountriesList = memoizeOne(computedCountriesList);

function valueOf(v) {
  if (!v) return '';
  if (typeof v === 'object') return v.value;
  return v;
}

export default class CountrySelector extends PureComponent {
  handleChange = (name, value) => {
    const { onChange } = this.props;
    onChange(name, valueOf(value));
  };

  expandValue = (value) => {
    const { lang, saveMode } = this.props;
    if (!value) return value;
    const options = memoizedCountriesList(lang, saveMode);
    return options.find((o) => o.value === valueOf(value));
  };

  render() {
    const { label, lang, mode, name, value, placeholder, required, summary, disabled, locked, saveMode } = this.props;
    const isLocked = isFieldLocked(name, locked);
    if (mode === 'summary') {
      if (summary?.hidden) {
        return null;
      }
      const labelValue = memoizedCountriesList(lang, saveMode).find((o) => o.value === valueOf(value))?.label;
      return <StringField label={summary?.label || label} value={labelValue} mode="summary" />;
    }

    return (
      <FieldContainer {...this.props}>
        <div className="af-country-select-container">
          <Select
            required={required}
            value={this.expandValue(value)}
            placeholder={placeholder || label}
            onChange={(data) => this.handleChange(name, data)}
            options={memoizedCountriesList(lang, saveMode)}
            isDisabled={disabled || isLocked}
          />
          {value && <input key={value} name={name} value={valueOf(value)} readOnly style={{ display: 'none' }} />}
        </div>
      </FieldContainer>
    );
  }
}

CountrySelector.defaultProps = {
  lang: 'fr',
  label: '',
  placeholder: '',
  required: false,
  value: undefined,
  saveMode: SaveMode.LABEL,
};

CountrySelector.propTypes = {
  label: PropTypes.string,
  lang: PropTypes.oneOf(['fr', 'en', 'de', 'es', 'pt']),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.string,
  saveMode: PropTypes.oneOf(Object.values(SaveMode)),
};
